import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Radar as RadarIcon, CalendarClock, Tag } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

/**
 * MatchRadarInsights — small analytics panel that lives directly above the
 * Match Radar toggle card in the Job Search view. Shows how many matches
 * the weekly radar surfaced in the last 30 days, the trend vs the previous
 * 30-day window, a compact 8-week sparkline and the top branches driving
 * the volume. Silently self-hides when the user has zero recorded runs so
 * we never render an empty state that scares brand-new users.
 */
export default function MatchRadarInsights({ enabled }) {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/preferences/match-radar/insights")
      .then((r) => { if (!cancelled) setData(r.data || null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled]);

  if (loading) return null;
  if (!data) return null;

  const series = Array.isArray(data.weekly_series) ? data.weekly_series : [];
  // Zero-runs guard: don't show the panel until we have at least one week
  // of data — new users would only see "0 matches" which is worse than
  // showing nothing at all.
  if (series.length === 0 && (data.total_30d || 0) === 0) return null;

  const trend = Number(data.trend_pct || 0);
  const trendKind = trend > 0 ? "up" : trend < 0 ? "down" : "flat";
  const trendColor = trendKind === "up"
    ? "text-emerald-700"
    : trendKind === "down"
      ? "text-rose-700"
      : "text-neutral-500";
  const TrendIcon = trendKind === "up" ? TrendingUp : trendKind === "down" ? TrendingDown : Minus;

  const dateFmt = new Intl.DateTimeFormat(lang || "nl", { day: "numeric", month: "short" });
  const lastRunLabel = data.last_run_at
    ? dateFmt.format(new Date(data.last_run_at))
    : "—";
  const nextRunLabel = data.next_run_at
    ? dateFmt.format(new Date(data.next_run_at))
    : "—";

  // Sparkline — normalize the last 8 weeks so a single spike doesn't
  // dwarf everything else. Missing weeks stay as height=0 bars.
  const bars = series.slice(-8);
  const maxCount = Math.max(1, ...bars.map((b) => b.count || 0));
  const topCats = Array.isArray(data.top_categories) ? data.top_categories.slice(0, 5) : [];
  const maxCatCount = Math.max(1, ...topCats.map((c) => c.count || 0));

  return (
    <div className="ed-card p-5" data-testid="jobsearch-radar-insights">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <RadarIcon size={13} strokeWidth={1.5} className="text-neutral-500" />
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
            {t("mrInsightsTitle")}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[0.65rem] font-mono uppercase tracking-[0.1em] text-neutral-500">
          <CalendarClock size={11} strokeWidth={1.5} />
          <span>{t("mrInsightsNextRun", { date: nextRunLabel })}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        {/* KPI: total matches */}
        <div>
          <p className="font-serif text-4xl leading-none" data-testid="mr-insights-total">
            {data.total_30d ?? 0}
          </p>
          <p className="text-xs text-neutral-600 mt-1">{t("mrInsights30dLabel")}</p>
        </div>
        {/* KPI: trend */}
        <div>
          <p className={`font-serif text-4xl leading-none inline-flex items-center gap-2 ${trendColor}`} data-testid="mr-insights-trend">
            <TrendIcon size={22} strokeWidth={1.75} />
            <span>{Math.abs(trend)}%</span>
          </p>
          <p className="text-xs text-neutral-600 mt-1">{t("mrInsightsTrendLabel")}</p>
        </div>
        {/* KPI: last run */}
        <div>
          <p className="font-serif text-4xl leading-none">{data.runs_30d ?? 0}</p>
          <p className="text-xs text-neutral-600 mt-1">
            {t("mrInsightsRunsLabel")}
            {data.last_run_at && (
              <span className="block font-mono text-[0.6rem] uppercase tracking-[0.1em] text-neutral-500 mt-1">
                {t("mrInsightsLastRun", { date: lastRunLabel })}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Sparkline */}
      {bars.length > 0 && (
        <div className="mb-5">
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">
            {t("mrInsightsWeeklyLabel")}
          </p>
          <div className="flex items-end gap-1.5 h-14" data-testid="mr-insights-sparkline">
            {bars.map((b, i) => {
              const h = Math.max(3, Math.round(((b.count || 0) / maxCount) * 100));
              return (
                <div key={b.week_start || i} className="flex-1 h-full flex flex-col justify-end" title={`${b.week_start}: ${b.count}`}>
                  <div
                    className="bg-neutral-800 rounded-sm transition-colors hover:bg-emerald-700"
                    style={{ height: `${h}%` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top branches */}
      {topCats.length > 0 && (
        <div>
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2 inline-flex items-center gap-1.5">
            <Tag size={11} strokeWidth={1.5} /> {t("mrInsightsTopCats")}
          </p>
          <ul className="space-y-2" data-testid="mr-insights-top-cats">
            {topCats.map((c) => (
              <li key={c.name} className="flex items-center gap-3">
                <span className="text-sm text-neutral-800 shrink-0 w-40 truncate" title={c.name}>{c.name}</span>
                <span className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                  <span
                    className="block h-full bg-emerald-700"
                    style={{ width: `${Math.max(6, Math.round((c.count / maxCatCount) * 100))}%` }}
                  />
                </span>
                <span className="font-mono text-[0.65rem] text-neutral-500 w-6 text-right">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
